/**
 * SUCURSALES PAGE - Branch Management
 * Gestión de sucursales del laboratorio
 *
 * ACCESO: Solo Administrador Global
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Building2,
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Clock,
  Phone,
  MapPin,
} from "lucide-react";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import {
  useSucursales,
  useActivateSucursal,
  useDeactivateSucursal,
  useDeleteSucursal,
  type Sucursal,
} from "@/hooks/use-sucursales";
import { isGlobalAdmin } from "@/contexts/sucursal-context";
import { useAuth } from "@/contexts/auth-context";

import { SucursalFormDialog } from "./components/sucursal-form-dialog";

export default function SucursalesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Verificar acceso - solo admin global
  useEffect(() => {
    if (!isGlobalAdmin(user)) {
      toast.error(t("api.permissionDenied"));
      navigate("/", { replace: true });
    }
  }, [navigate, t, user]);

  // TanStack Query - Cargar sucursales
  const { data: sucursales = [], isLoading, isError, error, refetch } = useSucursales();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState<Sucursal | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  // Mutations
  const activateMutation = useActivateSucursal({
    onSuccess: () => {
      toast.success(t("sucursales.toast.activated", { name: selectedSucursal?.nombre }));
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivateSucursal({
    onSuccess: () => {
      toast.warning(t("sucursales.toast.deactivated", { name: selectedSucursal?.nombre }));
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeleteSucursal({
    onSuccess: () => {
      toast.error(t("sucursales.toast.deleted", { name: selectedSucursal?.nombre }));
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleEdit = useCallback((sucursal: Sucursal) => {
    setSelectedSucursal(sucursal);
    setIsFormOpen(true);
  }, []);

  const handleToggleStatus = useCallback((sucursal: Sucursal) => {
    setSelectedSucursal(sucursal);
    setConfirmAction(sucursal.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((sucursal: Sucursal) => {
    setSelectedSucursal(sucursal);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleCreate = () => {
    setSelectedSucursal(null);
    setIsFormOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedSucursal || !confirmAction) return;

    if (confirmAction === "activate") {
      activateMutation.mutate(selectedSucursal.id);
    } else if (confirmAction === "deactivate") {
      deactivateMutation.mutate(selectedSucursal.id);
    } else if (confirmAction === "delete") {
      deleteMutation.mutate(selectedSucursal.id);
    }

    setIsConfirmOpen(false);
    setSelectedSucursal(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    toast.success(isNew ? t("sucursales.toast.created") : t("sucursales.toast.updated"));
    setSelectedSucursal(null);
  };

  // Format time for display
  const formatTime = (time: string | null | undefined) => {
    if (!time) return "-";
    return time.substring(0, 5); // "HH:MM:SS" -> "HH:MM"
  };

  // Column definitions
  const columns: ColumnDef<Sucursal>[] = useMemo(
    () => [
      {
        accessorKey: "codigo",
        header: t("sucursales.columns.codigo"),
        cell: ({ row }) => <span className="font-mono font-medium">{row.original.codigo}</span>,
      },
      {
        accessorKey: "nombre",
        header: t("sucursales.columns.nombre"),
        cell: ({ row }) => <span className="font-medium">{row.original.nombre}</span>,
      },
      {
        accessorKey: "direccion",
        header: t("sucursales.columns.direccion"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground max-w-[200px] truncate block">
            {row.original.direccion}
          </span>
        ),
      },
      {
        accessorKey: "telefono",
        header: t("sucursales.columns.telefono"),
      },
      {
        accessorKey: "horario",
        header: t("sucursales.columns.horario"),
        cell: ({ row }) => {
          if (row.original.opera_24_horas) {
            return (
              <Badge variant="outline" className="gap-1">
                <Clock className="h-3 w-3" />
                24h
              </Badge>
            );
          }
          return (
            <span className="text-sm">
              {formatTime(row.original.horario_apertura)} -{" "}
              {formatTime(row.original.horario_cierre)}
            </span>
          );
        },
      },
      {
        accessorKey: "is_active",
        header: t("sucursales.columns.status"),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: t("common.actions"),
        cell: ({ row }) => {
          const sucursal = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(sucursal)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(sucursal)}>
                  {sucursal.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!sucursal.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(sucursal)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  // Table instance
  const table = useReactTable({
    data: sucursales,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Mobile card render function
  const renderMobileCard = useCallback(
    (sucursal: Sucursal) => (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-medium text-sm">{sucursal.codigo}</span>
            <span className="font-medium">{sucursal.nombre}</span>
            <Badge variant={sucursal.is_active ? "default" : "secondary"} className="shrink-0">
              {sucursal.is_active ? t("common.active") : t("common.inactive")}
            </Badge>
          </div>
          {sucursal.direccion && (
            <div className="flex items-start gap-1 mt-2 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{sucursal.direccion}</span>
            </div>
          )}
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            {sucursal.telefono && (
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                <span>{sucursal.telefono}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {sucursal.opera_24_horas ? (
                <span>24h</span>
              ) : (
                <span>
                  {formatTime(sucursal.horario_apertura)} - {formatTime(sucursal.horario_cierre)}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-1 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleEdit(sucursal)}>
              <Pencil className="h-3 w-3 mr-1" />
              {t("common.edit")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleToggleStatus(sucursal)}>
                  {sucursal.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!sucursal.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(sucursal)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ),
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Building2 className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("sucursales.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("sucursales.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size={isMobile ? "sm" : "default"} onClick={handleCreate}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("sucursales.newSucursal")}
          </Button>
        </div>
      </div>

      {/* Error */}
      {isError && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{getErrorMessage(error)}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("sucursales.list")}</CardTitle>
          <CardDescription>
            {t("sucursales.showingResults", { count: sucursales.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("sucursales.noSucursales")}
            hideOnTablet={["direccion", "telefono"]}
            renderMobileCard={renderMobileCard}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <SucursalFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        sucursal={selectedSucursal}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("sucursales.confirmActivate")
            : confirmAction === "delete"
              ? t("sucursales.confirmDelete")
              : t("sucursales.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("sucursales.confirmActivateDesc", { name: selectedSucursal?.nombre })
            : confirmAction === "delete"
              ? t("sucursales.confirmDeleteDesc", { name: selectedSucursal?.nombre })
              : t("sucursales.confirmDeactivateDesc", { name: selectedSucursal?.nombre })
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
